import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import useAnimatedNavToggler from "../helpers/useAnimatedNavToggler";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../../FireBaseConfig";
import logo from "../../images/logo/logo-kz.svg";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from "react-i18next";

const Header = styled.header`
    ${tw`flex justify-between items-center max-w-screen-2xl mx-auto p-2 lg:p-4`}
`;

const GlobalStyles = createGlobalStyle`
    @font-face {
        font-family: 'SFUIText';
        font-style: italic;
        font-weight: normal;
    }
`;

const NavLinks = styled.div`
    ${tw`flex lg:items-center justify-start flex-1 gap-1 lg:gap-2`}
    max-width: 100%;
    overflow-x: auto;
    white-space: nowrap;
    
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
`;


const NavLink = styled.a`
    font-family: 'SFUIText', sans-serif;
    ${tw`text-sm lg:text-base my-2 lg:mx-2 lg:my-0 font-semibold tracking-wide transition duration-300 pb-1`}
    ${tw`hover:border-primary-500 hover:text-primary-500 focus:text-primary-500`}
    white-space: nowrap;
    text-align: center;
    min-width: fit-content;
    
    ${css`
        text-decoration: none;
        color: #1d324f;

        &:hover, &:focus {
            color: #0ABD19;
            ${props => (props.hasBorder ? tw`border-b-2 border-transparent` : tw`border-b-0`)};
        }
    `}
    
    html[lang="kz"] & {
        letter-spacing: -0.03em;
    }
`;

const pulsateAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const PrimaryLink = styled(NavLink)`
    ${tw`lg:mx-0 px-8 py-3 rounded-full text-white focus:shadow-outline border-b-0`}
    flex-shrink: 0;
    background-color: #0ABD19;
    border: none;
    transition: all 0.3s ease;

    &:not(:hover):not(:focus) {
        animation: ${pulsateAnimation} 1.5s infinite alternate;
    }

    &:hover, &:focus {
        background-color: #098E14;
        transform: scale(1.1);
        color: white;
    }
`;

const LogoLink = styled.a`
    ${tw`flex items-center font-black border-b-0 mr-10`}
    flex-shrink: 0;
    img { ${tw`w-40 h-auto`} }
`;

const RightActions = styled.div`
  ${tw`flex items-center gap-3 ml-4`}
  flex-shrink: 0;
`;

const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between lg:hidden`;

const NavToggle = styled.button`
  ${tw`lg:hidden z-20 focus:outline-none transition duration-300`}
  display: flex; align-items: center; justify-content: center;
  background: transparent; border: none;
  color: ${({ open }) => (open ? "#0ABD19" : "currentColor")};
  svg { path { stroke: ${({ open }) => (open ? "#0ABD19" : "currentColor")}; } }
`;

const iconStyles = tw`w-6 h-6`;

const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} { ${tw`flex flex-col items-center`} }
  ${RightActions} { ${tw`justify-center mt-4`} }
`);

const DesktopNavLinks = styled.nav`
    ${tw`hidden lg:flex items-center w-full`}
`;

const LanguageSwitcher = styled.div`
    ${tw`flex items-center space-x-2 ml-4`}
    flex-shrink: 0;
`;

const LanguageButton = styled.button`
  ${tw`px-2 py-1 text-sm font-medium border rounded focus:outline-none transition duration-300`}
  ${({ active }) =>
    active
        ? tw`bg-[#0ABD19] text-white border-[#0ABD19]`
        : tw`bg-white text-[#1d324f] border-gray-300 hover:bg-gray-100`}
`;

const collapseBreakPointCssMap = {
    sm: { mobileNavLinks: tw`sm:hidden`, desktopNavLinks: tw`sm:flex`, mobileNavLinksContainer: tw`sm:hidden` },
    md: { mobileNavLinks: tw`md:hidden`, desktopNavLinks: tw`md:flex`, mobileNavLinksContainer: tw`md:hidden` },
    lg: { mobileNavLinks: tw`lg:hidden`, desktopNavLinks: tw`lg:flex`, mobileNavLinksContainer: tw`lg:hidden` },
    xl: { mobileNavLinks: tw`lg:hidden`, desktopNavLinks: tw`lg:flex`, mobileNavLinksContainer: tw`lg:hidden` }
};

export default function HeaderComponent({
                                            roundedHeaderButton = false,
                                            logoLink,
                                            links,
                                            className,
                                            collapseBreakpointClass = "lg"
                                        }) {
    const { t, i18n } = useTranslation();
    const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
    const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const auth = getAuth(app);
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });
        return () => unsubscribe();
    }, []);

    const changeLang = (lng) => {
        i18n.changeLanguage(lng);
        document.documentElement.lang = lng;
    };

    const currentLng =
        i18n.language?.startsWith("kz") ? "kz" :
            i18n.language?.startsWith("ru") ? "ru" :
                i18n.resolvedLanguage || "ru";

    // Меню (прокручиваемая середина)
    const menuLinks = (
        <NavLinks key="menu">
            <NavLink href="/Tariffs">{t("nav.tariffs")}</NavLink>
            <NavLink href="/Rates">{t("nav.rates")}</NavLink>
            <NavLink href="/Shops">{t("nav.shops")}</NavLink>
            <NavLink href="/RedemptionOfGoods">{t("nav.redemption")}</NavLink>
            <NavLink href="/AboutUS">{t("nav.about")}</NavLink>
            <NavLink href="/Contacts">{t("nav.contacts")}</NavLink>
        </NavLinks>
    );

    const actions = (
        <RightActions key="actions">
            {!isAuthenticated ? (
                <>
                    <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="/Login">
                        {t("nav.login")}
                    </PrimaryLink>
                    <LanguageSwitcher>
                        <LanguageButton active={currentLng === "ru"} onClick={() => changeLang("ru")}>RU</LanguageButton>
                        <LanguageButton active={currentLng === "kz"} onClick={() => changeLang("kz")}>KZ</LanguageButton>
                    </LanguageSwitcher>
                </>
            ) : null}
        </RightActions>
    );


    const defaultLogoLink = (
        <LogoLink href="/">
            <img src={logo} alt="logo" />
        </LogoLink>
    );

    const finalLogoLink = logoLink || defaultLogoLink;
    console.log("Текущий язык:", i18n.language);
    console.log("Перевод nav.contacts:", t("nav.contacts"));

    return (
        <>
            <GlobalStyles />
            <Header className={className || "header-light"}>
                {/* Desktop */}
                <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
                    {finalLogoLink}
                    {menuLinks}
                    {actions}
                </DesktopNavLinks>

                {/* Mobile */}
                <MobileNavLinksContainer>
                    {finalLogoLink}
                    <NavToggle onClick={toggleNavbar} open={showNavLinks}>
                        {showNavLinks ? <CloseIcon css={iconStyles} /> : <MenuIcon css={iconStyles} />}
                    </NavToggle>

                    <MobileNavLinks
                        initial={{ x: "150%", display: "none" }}
                        animate={animation}
                        css={collapseBreakpointCss.mobileNavLinks}
                    >
                        {menuLinks}
                        {actions}
                    </MobileNavLinks>
                </MobileNavLinksContainer>
            </Header>
        </>
    );
}